import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingNotificationId, setSendingNotificationId] = useState(null);
  const [selectedAppTypes, setSelectedAppTypes] = useState([]);
  const [sending, setSending] = useState(false);

  const appTypeOptions = [
    { value: "ffskin", label: "FF Skin" },
    { value: "dailydiamonds", label: "Daily Diamonds" }
  ];

 const fetchNotifications = async () => {
    
  try {
    setLoading(true);
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${baseUrl}/notification/list?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const result = await res.json();

    if (result.data) {
      setNotifications(result.data);
      setTotal(result.total || 0);
    }
  } catch (error) {
    toast.error("Failed to fetch notifications");
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchNotifications();
  }, [page]);


 // Create or update notification 
 const handleCreateOrUpdate = async () => {
    if (!title || !message) {
      toast.error("All fields are required");
      return;
    }
  const token = localStorage.getItem("token");
  const url = editId
    ? `${baseUrl}/notification/edit/${editId}`
    : `${baseUrl}/notification/create`;
  const method = editId ? "PUT" : "POST";

  try {
    const res = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ title, message }),
    });

    const result = await res.json();

    if (res.ok) {
      setTitle("");
      setMessage("");
      setEditId(null);
      toast.success(editId ? "Updated successfully!" : "Created successfully!"); // ✅ Toast shown only once
      await fetchNotifications(); // 🔁 Don't put toast in this function
    } else {
      toast.error(result.message || "Something went wrong");
    }
  } catch (error) {
    toast.error("Server error");
  }
};

// Open send modal
const handleSend = (id) => {
  setSendingNotificationId(id);
  setSelectedAppTypes([]);
  setShowSendModal(true);
};

// Toggle appType selection
const toggleAppType = (value) => {
  setSelectedAppTypes((prev) =>
    prev.includes(value)
      ? prev.filter((v) => v !== value)
      : [...prev, value]
  );
};

// Confirm send notification
const handleSendConfirm = async () => {
  if (selectedAppTypes.length === 0) {
    toast.error("Please select at least one app type");
    return;
  }

  const token = localStorage.getItem("token");
  setSending(true);

  try {
    const res = await fetch(`${baseUrl}/notification/send-notification/${sendingNotificationId}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ appType: selectedAppTypes }),
    });

    const result = await res.json();

    if (res.ok) {
      toast.success("Notification sent successfully!");
      setShowSendModal(false);
    } else {
      toast.error(result.message || "Failed to send notification.");
    }
  } catch (error) {
    toast.error("Error sending notification.");
  } finally {
    setSending(false);
  }
};


// delete notification
  const handleDelete = async (id) => {
  const token = localStorage.getItem("token");
  try {
    const res = await fetch(`${baseUrl}/notification/delete/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await res.json();

    if (res.ok) {
      toast.success("Deleted successfully!"); // ✅ Show toast only here
      await fetchNotifications(); // Just refresh silently
    } else {
      toast.error(result.message || "Failed to delete.");
    }
  } catch {
    toast.error("Error deleting notification.");
  }
};

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="flex bg-gray-50 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Navbar />
        <div className="p-6">
        <Toaster position="top-right" />

        {/* Send Notification Modal */}
        {showSendModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Select App Type</h3>
              <p className="text-sm text-gray-500 mb-4">Choose which apps to send the notification to:</p>
              <div className="space-y-3">
                {appTypeOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                      selectedAppTypes.includes(option.value)
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedAppTypes.includes(option.value)}
                      onChange={() => toggleAppType(option.value)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-5 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSendModal(false);
                    setSelectedAppTypes([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendConfirm}
                  disabled={selectedAppTypes.length === 0 || sending}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        )}

        <h2 className="text-2xl font-bold mb-4 text-gray-800">Notifications</h2>

        {/* Create Notification */}
        <div className="bg-white p-4 rounded shadow-md border border-gray-200 mb-6">
            <input
            type="text"
            className="border border-gray-300 p-2 w-full mb-2 bg-gray-50 text-gray-800 rounded focus:ring-2 focus:ring-blue-500"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
            className="border border-gray-300 p-2 w-full mb-2 bg-gray-50 text-gray-800 rounded focus:ring-2 focus:ring-blue-500"
            placeholder="Message"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            />
            <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={handleCreateOrUpdate}
            >
            {editId ? "Update" : "Create"}
            </button>
        </div>


        {/* Notification List */}
        <div className="bg-white rounded shadow-md border border-gray-200">
            {loading ? (
            <p className="p-4 text-gray-600">Loading...</p>
            ) : (
            <table className="min-w-full">
                <thead>
                <tr className="bg-blue-600 text-white text-left">
                    <th className="p-3">Title</th>
                    <th className="p-3">Message</th>
                    <th className="p-3 text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                {notifications.length === 0 ? (
                    <tr>
                    <td colSpan={3} className="text-center p-4 text-gray-500">
                        No data found.
                    </td>
                    </tr>
                ) : (
                    notifications.map((noti) => (
                    <tr key={noti.id} className="border-b border-gray-200 hover:bg-gray-100 transition cursor-pointer text-gray-700">
                        <td className="p-3">{noti.title}</td>
                        <td className="p-3">{noti.message}</td>
                        <td className="p-3 text-center space-x-5">
                        <button
                            onClick={() => {
                            setEditId(noti.id);
                            setTitle(noti.title);
                            setMessage(noti.message);
                            }}
                            className="text-indigo-600 hover:underline"
                        >
                            Edit
                        </button>
                        <button
                                onClick={() => handleSend(noti.id)}
                                className="text-green-600 hover:underline"
                            >
                                Send
                            </button>
                        <button
                            onClick={() => handleDelete(noti.id)}
                            className="text-red-600 hover:underline"
                        >
                            Delete
                        </button>
                        </td>
                    </tr>
                    ))
                )}
                </tbody>
            </table>
            )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-2">
            <button
                disabled={page === 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-100 text-gray-700"
            >
                &laquo;
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
                <button
                key={pg}
                onClick={() => setPage(pg)}
                className={`px-4 py-2 border rounded ${
                    page === pg
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
                >
                {pg}
                </button>
            ))}

            <button
                disabled={page === totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                className="px-3 py-2 bg-white border border-gray-300 rounded hover:bg-gray-100 text-gray-700"
            >
                &raquo;
            </button>
            </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPage;
